import { Component, Input, OnInit, inject, model, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, ControlValueAccessor, AbstractControl, NgControl } from '@angular/forms';

@Component({
  selector: 'input-field',
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './input-field.component.html',
  styles: ``
})
export class InputFieldComponent implements OnInit, ControlValueAccessor {
  public ngControl = inject(NgControl, { optional: true, self: true });
  @ViewChild('inputElement', { static: false }) inputElement!: ElementRef<HTMLInputElement>;

  @Input() id: string = '';
  @Input() placeholder: string = '';
  @Input() type: string = 'text';
  @Input() label: string = '';
  @Input() errormessage: string = '';
  @Input() requiredLabel: boolean = false;
  @Input() disabled: boolean = false;
  @Input() customClass: string = '';
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() spellcheck: boolean | null = null;

  value = model<any>();
  onChange: any = () => {};
  onTouched: any = () => {};
  /** For `type="number"`: value to restore on blur when focus cleared a zero and the user left the field empty. */
  private numberValueToRestoreIfEmptyBlur: any = undefined;
  isPasswordVisible: boolean = false;
  passwordType: string = this.type;
  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }
  ngOnInit(): void {
    this.passwordType = this.type;
  }
  writeValue(value: any): void {
    this.value.set(value);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private isNumberTypeZeroDisplay(raw: any): boolean {
    if (raw === '' || raw === null || raw === undefined) {
      return false;
    }
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw).trim());
    return !isNaN(num) && num === 0;
  }

  onNumberFocus(): void {
    if (this.type !== 'number') {
      return;
    }
    const raw = this.value();
    if (this.isNumberTypeZeroDisplay(raw)) {
      this.numberValueToRestoreIfEmptyBlur = raw;
      this.value.set('');
    } else {
      this.numberValueToRestoreIfEmptyBlur = undefined;
    }
  }

  onNumberBlur(): void {
    if (this.type !== 'number') {
      this.onTouched();
      return;
    }
    const restore = this.numberValueToRestoreIfEmptyBlur;
    this.numberValueToRestoreIfEmptyBlur = undefined;

    if (restore !== undefined) {
      const raw = this.value();
      const empty = raw === '' || raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '');
      if (empty) {
        this.value.set(restore);
        this.onChange(restore);
      }
    }

    this.onTouched();
  }

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;
    if (this.type === 'number' && this.min !== null && this.min >= 0) {
      if (value.startsWith('-')) {
        value = value.replace('-', '');
        inputElement.value = value;
      }
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < 0) {
        value = '';
        inputElement.value = '';
      }
    }
    if (this.type === 'number' && this.max !== null && this.max >= 0) {
      if (value.startsWith('-')) {
        value = value.replace('-', '');
        inputElement.value = value;
      }
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > this.max) {
        value = this.max.toString();
        inputElement.value = this.max.toString();
      }
    }
    this.value.set(value);
    this.onChange(value);
  }
  onKeyDown(event: KeyboardEvent): void {
    if (this.type === 'number' && this.min !== null && this.min >= 0) {
      if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
        event.preventDefault();
      }
    }
  }
  onPaste(event: ClipboardEvent): void {
    if (this.type === 'number' && this.min !== null && this.min >= 0) {
      const pastedText = event.clipboardData?.getData('text') || '';
      const numValue = parseFloat(pastedText);
      if (!isNaN(numValue) && numValue < 0) {
        event.preventDefault();
      }
    }
  }

  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
    this.type = this.isPasswordVisible ? 'text' : 'password';
  }

  /**
   * Public method to programmatically focus this input field.
   * Optionally scrolls the field into view for better UX.
   * @param options - Optional configuration for focus behavior
   */
  public focus(options?: { scrollIntoView?: boolean }): void {
    if (this.inputElement?.nativeElement) {
      if (options?.scrollIntoView) {
        this.inputElement.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      this.inputElement.nativeElement.focus();
    }
  }
}
